import { useMemo } from "react";
import { useFeaturedProducts, useVariantsByProductIds } from "@/hooks/useProducts";
import ProductCard from "./ProductCard";

interface FeaturedProductsProps {
  categoryId: number | null;
  categoryName: string | null;
  onClearFilter: () => void;
}

export default function FeaturedProducts({
  categoryId,
  categoryName,
  onClearFilter,
}: FeaturedProductsProps) {
  const { products, loading, error, refetch } = useFeaturedProducts({
    limit: 12,
    categoryId,
  });

  const productIds = useMemo(() => products.map((p) => p.id), [products]);
  const { variantsByProduct } = useVariantsByProductIds(productIds);

  return (
    <section id="featured" className="w-full px-4 md:px-8 pt-2 pb-12 bg-background-50 scroll-mt-24">
      <div className="max-w-[1280px] mx-auto">
        {categoryId != null && categoryName && (
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-[13px] font-medium">
              {categoryName}
              <button
                type="button"
                onClick={onClearFilter}
                aria-label="Limpar filtro"
                className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-background-50/20 cursor-pointer"
              >
                <i className="ri-close-line text-[13px]"></i>
              </button>
            </span>
            <button
              type="button"
              onClick={onClearFilter}
              className="text-[13px] text-foreground-600 hover:text-primary-600 underline underline-offset-4 cursor-pointer whitespace-nowrap"
            >
              Limpar
            </button>
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square rounded-md bg-background-200/60"></div>
                <div className="h-3 bg-background-200/60 rounded mt-3 w-3/4"></div>
                <div className="h-3 bg-background-200/60 rounded mt-2 w-1/3"></div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="py-16 text-center">
            <p className="text-sm text-foreground-700">
              Não foi possível carregar: {error}
            </p>
            <button
              type="button"
              onClick={refetch}
              className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !error && products.length === 0 && (
          <div className="py-20 text-center">
            <p className="font-heading text-foreground-950 text-xl mb-2">
              Nada por aqui ainda.
            </p>
            <button
              type="button"
              onClick={onClearFilter}
              className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Ver todos
            </button>
          </div>
        )}

        {!loading && !error && products.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {products.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                variants={variantsByProduct[p.id] ?? []}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}